import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import { listData } from "../services/api";
import {
  createEvent,
  getEvent,
  publicEventUrl,
  syncEventStripe,
  updateEvent,
} from "./services/api";
import CouponsCard from "./components/CouponsCard";
import ExtraFieldsEditor from "./components/ExtraFieldsEditor";

function centsToDecimalString(cents) {
  if (cents == null || cents === "") return "";
  return (Number(cents) / 100).toFixed(2);
}

function decimalStringToCents(decimal) {
  if (decimal === "" || decimal == null) return null;
  const n = Number(String(decimal).replace(",", "."));
  if (!isFinite(n)) return null;
  return Math.round(n * 100);
}

function slugifyTitle(str) {
  return (str || "")
    .normalize("NFD")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase();
}

function isoToDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

const EMPTY_FORM = {
  channelId: "",
  slug: "",
  title: "",
  subtitle: "",
  status: "draft",
  eventMode: "paid",
  unitPriceDecimal: "",
  groupPriceDecimal: "",
  groupMinSize: "",
  groupMaxSize: "",
  currency: "BRL",
  capacity: "",
  startsAtLocal: "",
  endsAtLocal: "",
  timezone: "America/Sao_Paulo",
  locationName: "",
  locationAddress: "",
  motivationCodeId: "",
  extraFieldsSchema: [],
  confirmationEmailSubject: "",
  confirmationEmailBody: "",
  termsUrl: "",
};

export default function EventForm() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEdit = !!eventId;

  const [form, setForm] = useState(EMPTY_FORM);
  const [originalSlug, setOriginalSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const [channels, setChannels] = useState([]);
  const [motivationCodes, setMotivationCodes] = useState([]);

  const [stripeIds, setStripeIds] = useState({
    product: null,
    unit: null,
    group: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  function field(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // Auto-derive slug from title unless user already typed one manually.
  useEffect(() => {
    if (isEdit) return;
    if (slugTouched) return;
    field("slug", slugifyTitle(form.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  async function loadRefs() {
    const [channelsResp, motivResp] = await Promise.all([
      listData({
        table: "channels",
        term: "",
        value: "",
        limit: 0,
        cursor: "",
      }),
      listData({
        table: "motivation-codes",
        term: "",
        value: "",
        limit: 0,
        cursor: "",
      }),
    ]);
    if (channelsResp instanceof Error) {
      toast.error(channelsResp.message);
    } else {
      setChannels(channelsResp.data || []);
    }
    if (motivResp instanceof Error) {
      toast.error(motivResp.message);
    } else {
      setMotivationCodes(
        (motivResp.data || []).filter((c) => !c.count)
      );
    }
  }

  async function loadEvent() {
    if (!isEdit) return;
    const resp = await getEvent(eventId);
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    const e = resp.data;
    setForm({
      channelId: e.channel_id || "",
      slug: e.slug || "",
      title: e.title || "",
      subtitle: e.subtitle || "",
      status: e.status || "draft",
      eventMode: e.event_mode || "paid",
      unitPriceDecimal: centsToDecimalString(e.unit_price_cents),
      groupPriceDecimal: centsToDecimalString(e.group_price_cents),
      groupMinSize: e.group_min_size ?? "",
      groupMaxSize: e.group_max_size ?? "",
      currency: e.currency || "BRL",
      capacity: e.capacity ?? "",
      startsAtLocal: isoToDatetimeLocal(e.starts_at),
      endsAtLocal: isoToDatetimeLocal(e.ends_at),
      timezone: e.timezone || "America/Sao_Paulo",
      locationName: e.location_name || "",
      locationAddress: e.location_address || "",
      motivationCodeId: e.motivation_code_id || "",
      extraFieldsSchema: Array.isArray(e.extra_fields_schema)
        ? e.extra_fields_schema
        : [],
      confirmationEmailSubject: e.confirmation_email_subject || "",
      confirmationEmailBody: e.confirmation_email_body || "",
      termsUrl: e.terms_url || "",
    });
    setOriginalSlug(e.slug || "");
    setStripeIds({
      product: e.stripe_product_id || null,
      unit: e.stripe_unit_price_id || null,
      group: e.stripe_group_price_id || null,
    });
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadRefs();
        if (isEdit) await loadEvent();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  function buildPayload() {
    const startsAt = datetimeLocalToIso(form.startsAtLocal);
    const endsAt = form.endsAtLocal
      ? datetimeLocalToIso(form.endsAtLocal)
      : null;

    const payload = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      subtitle: form.subtitle.trim() || undefined,
      status: form.status,
      eventMode: form.eventMode,
      currency: form.currency || "BRL",
      capacity:
        form.capacity === "" || form.capacity == null
          ? null
          : Number(form.capacity),
      startsAt,
      endsAt,
      timezone: form.timezone || "America/Sao_Paulo",
      locationName: form.locationName.trim() || undefined,
      locationAddress: form.locationAddress.trim() || undefined,
      motivationCodeId: form.motivationCodeId || null,
      extraFieldsSchema: form.extraFieldsSchema || [],
      confirmationEmailSubject: form.confirmationEmailSubject.trim() || undefined,
      confirmationEmailBody: form.confirmationEmailBody.trim() || undefined,
      termsUrl: form.termsUrl.trim() || undefined,
    };

    if (form.eventMode === "paid") {
      payload.unitPriceCents = decimalStringToCents(form.unitPriceDecimal);
      payload.groupPriceCents =
        form.groupPriceDecimal === ""
          ? null
          : decimalStringToCents(form.groupPriceDecimal);
      payload.groupMinSize =
        form.groupMinSize === "" ? null : Number(form.groupMinSize);
      payload.groupMaxSize =
        form.groupMaxSize === "" ? null : Number(form.groupMaxSize);
    } else {
      payload.unitPriceCents = null;
      payload.groupPriceCents = null;
      payload.groupMinSize = null;
      payload.groupMaxSize = null;
    }

    if (!isEdit) {
      payload.channelId = form.channelId;
    }

    return payload;
  }

  function validate(payload) {
    const errors = [];
    if (!isEdit && !payload.channelId) errors.push("Channel obrigatório.");
    if (!payload.slug || !/^[a-z0-9-]+$/.test(payload.slug))
      errors.push("Slug inválido (use minúsculas, números e hífens).");
    if (!payload.title || payload.title.length < 2)
      errors.push("Título obrigatório (min 2 caracteres).");
    if (!payload.startsAt) errors.push("Data de início obrigatória.");
    if (payload.eventMode === "paid") {
      if (payload.unitPriceCents == null || payload.unitPriceCents < 0)
        errors.push("Preço unitário obrigatório (>= 0) para evento pago.");
      if (
        payload.groupPriceCents != null &&
        (payload.groupMinSize == null || payload.groupMinSize < 2)
      )
        errors.push(
          "Tamanho mínimo de grupo >= 2 é obrigatório quando há preço de grupo."
        );
      if (
        payload.groupMinSize != null &&
        payload.groupMaxSize != null &&
        payload.groupMaxSize < payload.groupMinSize
      )
        errors.push("Tamanho máximo de grupo deve ser >= mínimo.");
    }
    if (payload.eventMode === "free_with_donation" && !payload.motivationCodeId)
      errors.push(
        "Motivation Code obrigatório para eventos free_with_donation."
      );
    return errors;
  }

  async function handleSave() {
    const payload = buildPayload();
    const errors = validate(payload);
    if (errors.length > 0) {
      toast.error(errors.join(" · "));
      return;
    }
    setSaving(true);
    const resp = isEdit
      ? await updateEvent(eventId, payload)
      : await createEvent(payload);
    setSaving(false);
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    toast.success(isEdit ? "Evento atualizado" : "Evento criado");
    if (!isEdit) {
      navigate(`/tools/events/${resp.data.id}/edit`, { replace: true });
    } else {
      setOriginalSlug(resp.data.slug);
      setStripeIds({
        product: resp.data.stripe_product_id || null,
        unit: resp.data.stripe_unit_price_id || null,
        group: resp.data.stripe_group_price_id || null,
      });
    }
  }

  async function handleSyncStripe() {
    if (!eventId) return;
    setSyncing(true);
    const resp = await syncEventStripe(eventId);
    setSyncing(false);
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    toast.success("Stripe sincronizado");
    const e = resp.data;
    setStripeIds({
      product: e.stripe_product_id || null,
      unit: e.stripe_unit_price_id || null,
      group: e.stripe_group_price_id || null,
    });
  }

  if (loading) {
    return (
      <div className="w-full text-center py-10">
        <Spinner />
      </div>
    );
  }

  const slugPreview = form.slug ? `/eventos/${form.slug}` : "/eventos/—";

  return (
    <div className="w-full max-w-[1100px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => navigate("/tools/events")}
            className="text-slate-500 text-xs uppercase tracking-wider hover:text-blue-600 bg-transparent border-0 p-0 text-left"
          >
            ← Eventos
          </button>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            {isEdit ? "Editar evento" : "Novo evento"}
          </h1>
          {isEdit && (
            <span className="text-xs text-slate-500 font-mono">
              {slugPreview}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" /> Salvando...
              </>
            ) : isEdit ? (
              "Salvar"
            ) : (
              "Criar"
            )}
          </Button>
          {isEdit && (
            <>
              <Button
                variant="outline-success"
                href={publicEventUrl(originalSlug)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ver
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => navigate(`/tools/events/${eventId}/content`)}
              >
                Editar conteúdo
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleSyncStripe}
                disabled={syncing || form.eventMode !== "paid"}
                title={
                  form.eventMode !== "paid"
                    ? "Disponível apenas para eventos pagos"
                    : ""
                }
              >
                {syncing ? "Sincronizando..." : "Sync Stripe"}
              </Button>
            </>
          )}
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </div>
      </div>

      {isEdit && form.eventMode === "paid" && (
        <Alert variant="info" className="mb-4">
          <div className="d-flex flex-column gap-1">
            <strong>Stripe</strong>
            <span className="small">
              Produto:{" "}
              <code>{stripeIds.product || "— não sincronizado"}</code>
            </span>
            <span className="small">
              Preço unitário: <code>{stripeIds.unit || "—"}</code>
            </span>
            <span className="small">
              Preço grupo: <code>{stripeIds.group || "—"}</code>
            </span>
          </div>
        </Alert>
      )}

      {form.eventMode === "free_with_donation" && (
        <Alert variant="warning" className="mb-4">
          <strong>Atenção — Motivation Code:</strong> o código informado precisa
          estar cadastrado no Salesforce CRM para que a doação sincronize
          corretamente.
        </Alert>
      )}

      <Card className="border-0 shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
            Identificação
          </h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Group>
            <Form.Label>Título *</Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => field("title", e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Slug *</Form.Label>
            <Form.Control
              type="text"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                field(
                  "slug",
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")
                );
              }}
            />
            <Form.Text className="text-xs text-slate-500">
              URL pública: <code>{slugPreview}</code>
              {isEdit && form.slug !== originalSlug && (
                <span className="text-amber-600 ms-2">
                  (alterar o slug quebra links já compartilhados)
                </span>
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group className="md:col-span-2">
            <Form.Label>Subtítulo</Form.Label>
            <Form.Control
              type="text"
              value={form.subtitle}
              onChange={(e) => field("subtitle", e.target.value)}
            />
          </Form.Group>

          {!isEdit && (
            <Form.Group>
              <Form.Label>Channel *</Form.Label>
              <Form.Select
                value={form.channelId}
                onChange={(e) => field("channelId", e.target.value)}
              >
                <option value="">-- selecionar --</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => field("status", e.target.value)}
            >
              <option value="draft">Rascunho</option>
              <option value="published">Publicado</option>
              <option value="sold_out">Esgotado</option>
              <option value="archived">Arquivado</option>
            </Form.Select>
          </Form.Group>
        </div>
      </Card>

      <Card className="border-0 shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
            Modo & Preço
          </h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Group className="md:col-span-2">
            <Form.Label>Modo *</Form.Label>
            <Form.Select
              value={form.eventMode}
              onChange={(e) => field("eventMode", e.target.value)}
            >
              <option value="paid">Pago</option>
              <option value="free_with_donation">Grátis com doação</option>
            </Form.Select>
            <Form.Text className="text-xs text-slate-500">
              Eventos pagos usam Stripe (cartão, Pix, boleto). Eventos
              "free_with_donation" usam a infraestrutura Asaas existente.
            </Form.Text>
          </Form.Group>

          {form.eventMode === "paid" ? (
            <>
              <Form.Group>
                <Form.Label>Preço unitário (R$) *</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="ex.: 50.00"
                  value={form.unitPriceDecimal}
                  onChange={(e) => field("unitPriceDecimal", e.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Moeda</Form.Label>
                <Form.Select
                  value={form.currency}
                  onChange={(e) => field("currency", e.target.value)}
                >
                  <option value="BRL">BRL</option>
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Preço de grupo (R$)</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="ex.: 40.00 (opcional)"
                  value={form.groupPriceDecimal}
                  onChange={(e) => field("groupPriceDecimal", e.target.value)}
                />
                <Form.Text className="text-xs text-slate-500">
                  Se preenchido, substitui o unitário quando o número de
                  participantes atinge o mínimo definido abaixo.
                </Form.Text>
              </Form.Group>

              <div className="grid grid-cols-2 gap-3">
                <Form.Group>
                  <Form.Label>Tamanho mín. grupo</Form.Label>
                  <Form.Control
                    type="number"
                    min={2}
                    placeholder="ex.: 10"
                    value={form.groupMinSize}
                    onChange={(e) => field("groupMinSize", e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Tamanho máx. grupo</Form.Label>
                  <Form.Control
                    type="number"
                    min={2}
                    placeholder="opcional"
                    value={form.groupMaxSize}
                    onChange={(e) => field("groupMaxSize", e.target.value)}
                  />
                </Form.Group>
              </div>
            </>
          ) : (
            <Form.Group className="md:col-span-2">
              <Form.Label>Motivation Code *</Form.Label>
              <Form.Select
                value={form.motivationCodeId}
                onChange={(e) => field("motivationCodeId", e.target.value)}
              >
                <option value="">-- selecionar --</option>
                {motivationCodes
                  .filter(
                    (mc) => !form.channelId || mc.channel_id === form.channelId
                  )
                  .map((mc) => (
                    <option key={mc.motivation_code} value={mc.motivation_code}>
                      {mc.motivation_code} — {mc.description}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text className="text-xs text-slate-500">
                Apenas motivation codes do channel selecionado. As doações
                opcionais serão lançadas nesse motivation code via fluxo Asaas
                existente.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label>Capacidade total</Form.Label>
            <Form.Control
              type="number"
              min={1}
              placeholder="ilimitado"
              value={form.capacity}
              onChange={(e) => field("capacity", e.target.value)}
            />
            <Form.Text className="text-xs text-slate-500">
              Deixe em branco para ilimitado. A contagem considera pending,
              confirmed e checked_in.
            </Form.Text>
          </Form.Group>
        </div>
      </Card>

      <Card className="border-0 shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
            Quando & Onde
          </h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Group>
            <Form.Label>Início *</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.startsAtLocal}
              onChange={(e) => field("startsAtLocal", e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Fim</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.endsAtLocal}
              onChange={(e) => field("endsAtLocal", e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Fuso horário</Form.Label>
            <Form.Control
              type="text"
              value={form.timezone}
              onChange={(e) => field("timezone", e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Nome do local</Form.Label>
            <Form.Control
              type="text"
              value={form.locationName}
              placeholder="ex.: Auditório Pão Diário"
              onChange={(e) => field("locationName", e.target.value)}
            />
          </Form.Group>
          <Form.Group className="md:col-span-2">
            <Form.Label>Endereço</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.locationAddress}
              placeholder="Rua, nº, bairro, cidade – UF"
              onChange={(e) => field("locationAddress", e.target.value)}
            />
          </Form.Group>
        </div>
      </Card>

      <Card className="border-0 shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
            Campos extras
          </h2>
        </div>
        <div className="p-5">
          <ExtraFieldsEditor
            schema={form.extraFieldsSchema}
            onChange={(next) => field("extraFieldsSchema", next)}
          />
        </div>
      </Card>

      <Card className="border-0 shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
            E-mail de confirmação
          </h2>
          <p className="text-xs text-slate-500 mt-1 mb-0">
            Conteúdo específico desse evento. O e-mail final usa o template
            Mailjet configurado no ambiente e injeta esse corpo customizado.
          </p>
        </div>
        <div className="p-5 grid grid-cols-1 gap-4">
          <Form.Group>
            <Form.Label>Assunto</Form.Label>
            <Form.Control
              type="text"
              value={form.confirmationEmailSubject}
              onChange={(e) =>
                field("confirmationEmailSubject", e.target.value)
              }
              placeholder="ex.: Sua inscrição no Retiro Pão Diário está confirmada"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Corpo (HTML ou texto)</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={form.confirmationEmailBody}
              onChange={(e) => field("confirmationEmailBody", e.target.value)}
              placeholder="Mensagem que aparecerá no e-mail. Pode conter HTML."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>URL dos termos / política</Form.Label>
            <Form.Control
              type="text"
              value={form.termsUrl}
              onChange={(e) => field("termsUrl", e.target.value)}
              placeholder="https://..."
            />
          </Form.Group>
        </div>
      </Card>

      <CouponsCard
        eventId={isEdit ? eventId : null}
        groupMinSize={
          form.groupMinSize ? parseInt(form.groupMinSize, 10) : null
        }
        eventMode={form.eventMode}
      />

      <div className="flex justify-end gap-2 pb-8">
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Salvando..." : isEdit ? "Salvar" : "Criar"}
        </Button>
      </div>
    </div>
  );
}
